import { zodResolver } from '@hookform/resolvers/zod';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { can } from '../../workspaces/workspaces-api';
import { RoleBadge } from './role-badge';
import { useCurrentWorkspace } from '../../workspaces/current-workspace';
import { Select } from '../../components/ui/select';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'validation.required')
    .max(254, 'validation.tooLong')
    .pipe(z.email('validation.email')),
  role: z.enum(['member', 'owner']),
});
type Values = z.infer<typeof schema>;

/** Приглашения пространства — владельцу: пригласить, отправить снова, отозвать. */
export function InvitationsPage() {
  const { t, locale } = useI18n();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const key = ['workspaces', workspace.id, 'invitations'];
  const path = { workspaceId: workspace.id };
  const pending = useInfiniteQuery({
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/workspaces/{workspaceId}/invitations', {
          params: { path, query: pageParam === undefined ? {} : { cursor: pageParam } },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', role: 'member' },
  });
  const invite = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(
        await api.POST('/api/v1/workspaces/{workspaceId}/invitations', {
          params: { path },
          body: values,
        }),
      ),
    onSuccess: async (invitation) => {
      setSentTo(invitation.email);
      form.reset({ email: '', role: form.getValues('role') });
      await refresh();
    },
  });
  const resend = useMutation({
    mutationFn: async (invitationId: string) =>
      unwrap(
        await api.POST('/api/v1/workspaces/{workspaceId}/invitations/{invitationId}/resend', {
          params: { path: { ...path, invitationId } },
        }),
      ),
    onSuccess: async (invitation) => {
      setSentTo(invitation.email);
      await refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: async (invitationId: string) =>
      ensureOk(
        await api.DELETE('/api/v1/workspaces/{workspaceId}/invitations/{invitationId}', {
          params: { path: { ...path, invitationId } },
        }),
      ),
    onSuccess: refresh,
  });
  const manage = can(workspace, 'invitations.manage');
  const items = pending.data?.pages.flatMap((page) => page.items) ?? [];
  const error = invite.error ?? resend.error ?? revoke.error ?? pending.error;

  return (
    <div className="space-y-6">
      {manage && (
        <Card title={t('invitations.new.title')}>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={form.handleSubmit((values) => invite.mutate(values))}
            noValidate
          >
            <div className="min-w-56 flex-1">
              <TextField
                control={form.control}
                name="email"
                type="email"
                label={t('auth.field.email')}
                autoComplete="off"
              />
            </div>
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <div className="flex flex-col gap-1.5 text-sm font-medium">
                  {t('members.role')}
                  <Select<'member' | 'owner'>
                    aria-label={t('members.role')}
                    value={field.value}
                    onValueChange={field.onChange}
                    options={[
                      { value: 'member', label: t('workspaces.role.member') },
                      { value: 'owner', label: t('workspaces.role.owner') },
                    ]}
                  />
                </div>
              )}
            />
            <Button type="submit" disabled={invite.isPending}>
              {t('invitations.new.submit')}
            </Button>
          </form>
          {sentTo !== null && (
            <Notice tone="success">
              {t('invitations.sent')} {sentTo}
            </Notice>
          )}
        </Card>
      )}
      <Card title={t('invitations.pending.title')}>
        {items.length === 0 && pending.isSuccess && (
          <p className="text-sm text-muted-foreground">{t('invitations.pending.empty')}</p>
        )}
        <ul className="divide-y divide-border text-sm">
          {items.map((invitation) => (
            <li key={invitation.id} className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <p className="truncate">
                  {invitation.email} <RoleBadge role={invitation.role} />
                </p>
                <p
                  className={`text-2xs ${invitation.expired ? 'text-danger' : 'text-muted-foreground'}`}
                >
                  {invitation.expired ? t('invitations.expired') : t('invitations.expires')}{' '}
                  {new Date(invitation.expiresAt).toLocaleDateString(locale)}
                </p>
              </div>
              {manage && (
                <span className="flex gap-3">
                  <Button
                    type="button"
                    variant="link"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(invitation.id)}
                  >
                    {t('invitations.resend')}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    {t('invitations.revoke')}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {pending.hasNextPage && (
          <Button type="button" variant="secondary" onClick={() => void pending.fetchNextPage()}>
            {t('common.showMore')}
          </Button>
        )}
      </Card>
      {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
    </div>
  );
}
